<template>
  <div class="pot-sm-container">
    <slot name="top-highlight" />
    <GridContainer padding>
      <GridRow
        class="pot-sm-container__row"
        clear-padding
      >
        <GridColumn
          v-for="(teaser, index) in teasersWithOffer"
          :key="index"
          :sm="tileGridSize"
          class="pot-sm-container__column"
        >
          <ProductOfferTile
            :animation-delay="setDelayAnimation(teaser.animationDelay || 0, index)"
            :background-color="teaser.bgColor"
            :description="teaser.copy"
            :heading-tag="headingTag"
            :offer-alternatives="teaser.offerAlternatives"
            :tile-size="tileSize"
            :title="teaser.teaserTitle"
            :show-available-variant="showAvailableVariant"
            price-variant="medium"
            animate-tile
            no-color-title
            link-tag="nuxt-link"
            class="pot-sm-container__offer"
          >
            <template #brand>
              <CdnImage
                v-if="teaser.brand?.logo && showBrandLogo"
                :src="teaser.brand.logo"
                :alt="`${teaser.brand.name} Logo`"
                :image-style="`aspect-ratio: ${teaser.brand.aspectRatio}`"
                class="pot-sm-container__logo"
                lazy
                render-without-figure
              />
            </template>
            <template #productImage="{ image }">
              <GoliathImageElement
                alt=""
                aria-hidden="true"
                :image="image"
                lazy
                class="pot-sm-container__img"
              />
            </template>
            <template #productImageHover="{ image }">
              <GoliathImageElement
                alt=""
                aria-hidden="true"
                :image="image"
                class="pot-sm-container__img"
                lazy
              />
            </template>
            <template #button="{ activeVariant }">
              <AddToCartButtonContainer
                ref="cartButton"
                :display-text="false"
                :offer="activeVariant?.offer"
                :product-name="activeVariant?.offer?.product?.name"
              />
            </template>
            <template #energy="{ energyLabel, activeVariant }">
              <EnergyLabelContainer
                v-if="energyLabel"
                :product="activeVariant?.offer?.product"
                datasheet-alignment="justify"
              />
            </template>
          </ProductOfferTile>
        </GridColumn>
      </GridRow>
    </GridContainer>
  </div>
</template>

<script lang="ts" setup>
import { computed, withDefaults } from 'vue';
import {
  GridColumn,
  GridRow,
  GridContainer,
  ProductOfferTile,
  GoliathImageElement,
} from '@i22/td-longtail-vue-components';
import { getFirstOfferAlternatives, type TransformedAlternative } from '@i22/rocket';
import AddToCartButtonContainer from '~/components/product-detail/add-to-cart-button-container.vue';
import EnergyLabelContainer from '@/components/energy-label-container.vue';
import type { ScopedOffer } from '~/lib/goliath/offers';
import type { Promotion, PromotionOffer } from '~/lib/offers-by-group';
import { getBrand, type BrandWithLogo } from '~/lib/brand';
import CdnImage from '~/components/cdn-image.vue';

export type ProductOfferTileSMPromotion = Promotion<{
  animationDelay?: number;
  showLocalTitle?: boolean;
  title?: string;
  bgColor?: string;
  copy?: string;
}>;

export type ProductOfferTileSMComponentProps = {
  items?: PromotionOffer<ProductOfferTileSMPromotion>[];
  noDelayAnimation?: boolean;
  headingTag?: string;
  tileType?: 'small' | 'medium' | undefined;
  showAvailableVariant?: boolean;
  showBrandLogo?: boolean;
};

const props = withDefaults(defineProps<ProductOfferTileSMComponentProps>(), {
  tileType: 'small',
  items: undefined,
  headingTag: undefined,
});

const tileGridSize = computed(() => (props.tileType === 'small' ? 4 : 6));
const tileSize = computed(() => (props.tileType === 'small' ? 's' : 'm'));

const setDelayAnimation = (delay: number, index: number) => {
  if (props.noDelayAnimation || delay === 0) return 0;
  if (delay >= 0) return delay;

  if (index >= 0) {
    return (index % (props.tileType === 'small' ? 3 : 2)) + 1;
  }
  // eslint-disable-next-line no-console
  console.warn('index is required');
  return 0;
};

type TeasersWithOffer = PromotionOffer<ProductOfferTileSMPromotion> & {
  offerAlternatives: TransformedAlternative<ScopedOffer>;
  teaserTitle: string;
  brand: BrandWithLogo | undefined;
};
const teasersWithOffer = computed<TeasersWithOffer[]>(() =>
  props.items.map((promotion: PromotionOffer<ProductOfferTileSMPromotion>) => ({
    ...promotion,
    teaserTitle:
      promotion.showLocalTitle && promotion.title
        ? promotion.title
        : promotion.offer?.product?.family?.name || '',
    offerAlternatives: getFirstOfferAlternatives(promotion.offer?.productAlternatives || []),
    brand: getBrand(promotion.offer),
  }))
);

defineExpose({
  tileGridSize,
  tileSize,
  setDelayAnimation,
});
</script>

<style lang="scss" scoped>
@use 'assets/base' as *;

.pot-sm-container {
  :deep(.product-offer-color-select__item) {
    padding-bottom: 6px;
  }

  :deep(.price__amount--highlight) {
    font-weight: 700;
  }

  :deep(.product-offer-color-select__list) {
    flex-wrap: wrap;
    max-width: 100%;
  }

  :deep(.product-offer-tile__product-image-link) {
    width: 100%;
  }
  :deep(.product-offer-detail-link__link.link-button) {
    display: block;
  }

  &__img {
    :deep(img) {
      width: 100%;
      aspect-ratio: 1 / 1;
    }
  }

  &__column {
    margin-bottom: $grid-base;
    @include mq($mq-sm) {
      margin-bottom: $grid-base * 2;
    }
  }

  &__logo {
    height: 25px;
  }
}
</style>
